// ReportForm.js - Bug report / Feature request form
import React, { useState } from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';
import api from '../../utils/api';
import useStore from '../../store/useStore';
import { captureCanvas } from './CanvasCapture';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.xs};
`;

const Label = styled.label`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.md};
  font-weight: 500;
`;

const Input = styled.input`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.md};

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const TextArea = styled.textarea`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.md};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const Select = styled.select`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.md};

  &:focus {
    outline: none;
    border-color: ${THEME.inputBorderFocus};
  }
`;

const AttachmentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.sm};
`;

const AttachmentLabel = styled.div`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.sm};
`;

const AttachmentButtons = styled.div`
  display: flex;
  gap: ${THEME.spacing.md};
  flex-wrap: wrap;
`;

const AttachmentButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.sm};
  padding: ${THEME.spacing.sm} ${THEME.spacing.md};
  background: ${props => props.$attached ? THEME.success : THEME.buttonSecondary};
  color: ${THEME.textPrimary};
  border: none;
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.sm};
  cursor: pointer;
  transition: background ${THEME.transitionFast};

  &:hover:not(:disabled) {
    background: ${props => props.$attached ? THEME.success : THEME.inputBorder};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AttachmentIcon = styled.span`
  font-size: ${THEME.fontSize.lg};
`;

const SubmitButton = styled.button`
  padding: ${THEME.spacing.md} ${THEME.spacing.lg};
  background: ${THEME.buttonPrimary};
  color: white;
  border: none;
  border-radius: ${THEME.borderRadiusMedium};
  font-size: ${THEME.fontSize.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover:not(:disabled) {
    background: ${THEME.buttonHover};
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${THEME.buttonDisabled};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${THEME.error};
  font-size: ${THEME.fontSize.sm};
  padding: ${THEME.spacing.sm};
  background: rgba(255, 165, 0, 0.1);
  border-radius: ${THEME.borderRadiusSmall};
`;

const SuccessMessage = styled.div`
  color: ${THEME.success};
  font-size: ${THEME.fontSize.md};
  padding: ${THEME.spacing.md};
  background: rgba(76, 175, 80, 0.1);
  border-radius: ${THEME.borderRadiusSmall};
  text-align: center;
`;

const AREA_OPTIONS = [
  { value: '', label: 'Select area (optional)' },
  { value: 'canvas', label: 'Canvas / Node Editor' },
  { value: 'components', label: 'Component Library' },
  { value: 'calculations', label: 'Calculations / Results' },
  { value: 'plots', label: 'Plots / Charts' },
  { value: 'smith-chart', label: 'Smith Chart' },
  { value: 'pdf-export', label: 'PDF Export' },
  { value: 'projects', label: 'Project Save / Load' },
  { value: 'ui', label: 'User Interface' },
  { value: 'performance', label: 'Performance' },
  { value: 'other', label: 'Other' }
];

const ReportForm = ({ type, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [area, setArea] = useState('');
  const [canvasImage, setCanvasImage] = useState(null);
  const [projectJson, setProjectJson] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const { nodes, edges, currentProject, frequencyMode, frequencySingle, frequencyStart, frequencyStop, frequencyStep } = useStore();

  const handleCaptureCanvas = async () => {
    try {
      const blob = await captureCanvas();
      if (blob) {
        setCanvasImage(blob);
      }
    } catch (err) {
      console.error('Failed to capture canvas:', err);
      api.track('error', 'client_error', `Feedback canvas capture: ${err.message}`);
      setError('Failed to capture canvas image');
    }
  };

  const handleAttachProject = () => {
    // Create project JSON from current state
    const projectData = {
      name: currentProject?.name || 'Untitled',
      description: currentProject?.description || '',
      frequencyMode,
      frequencySingle,
      frequencyStart,
      frequencyStop,
      frequencyStep,
      nodes: nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: node.data
      })),
      edges: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        sourceHandle: edge.sourceHandle,
        target: edge.target,
        targetHandle: edge.targetHandle
      }))
    };

    const jsonString = JSON.stringify(projectData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    setProjectJson(blob);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      if (area) {
        formData.append('area', area);
      }
      if (canvasImage) {
        formData.append('canvasImage', canvasImage, 'canvas.png');
      }
      if (projectJson) {
        formData.append('projectJson', projectJson, 'project.json');
      }

      await api.submitFeedback(formData);
      setSuccess(true);

      // Reset form
      setTitle('');
      setDescription('');
      setArea('');
      setCanvasImage(null);
      setProjectJson(null);

      // Notify parent
      setTimeout(() => {
        setSuccess(false);
        if (onSuccess) onSuccess();
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <SuccessMessage>
        Thank you! Your {type === 'bug' ? 'bug report' : 'feature request'} has been submitted.
      </SuccessMessage>
    );
  }

  const isBug = type === 'bug';

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup>
        <Label>{isBug ? 'Bug Title' : 'Feature Title'}</Label>
        <Input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={isBug ? 'Brief description of the bug' : 'Brief description of the feature'}
          maxLength={200}
        />
      </FormGroup>

      <FormGroup>
        <Label>Description</Label>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={isBug
            ? 'What happened? What did you expect to happen? Steps to reproduce...'
            : 'Describe the feature you\'d like to see. How would it help you?'
          }
        />
      </FormGroup>

      <FormGroup>
        <Label>Area</Label>
        <Select value={area} onChange={(e) => setArea(e.target.value)}>
          {AREA_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </FormGroup>

      <AttachmentSection>
        <AttachmentLabel>Attachments (optional)</AttachmentLabel>
        <AttachmentButtons>
          <AttachmentButton
            type="button"
            onClick={handleCaptureCanvas}
            $attached={!!canvasImage}
            disabled={submitting}
          >
            <AttachmentIcon>{canvasImage ? '✓' : '📷'}</AttachmentIcon>
            {canvasImage ? 'Canvas Image Attached' : 'Attach Canvas Image'}
          </AttachmentButton>

          <AttachmentButton
            type="button"
            onClick={handleAttachProject}
            $attached={!!projectJson}
            disabled={submitting}
          >
            <AttachmentIcon>{projectJson ? '✓' : '📁'}</AttachmentIcon>
            {projectJson ? 'Project File Attached' : 'Attach Project File'}
          </AttachmentButton>
        </AttachmentButtons>
      </AttachmentSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SubmitButton type="submit" disabled={submitting}>
        {submitting ? 'Submitting...' : `Submit ${isBug ? 'Bug Report' : 'Feature Request'}`}
      </SubmitButton>
    </Form>
  );
};

export default ReportForm;
